import { isNull, isUndefined } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { WATCHLIST_ENTITY } from '../../../constants/entity';
import { Entity, Lei, EntityModalType, EntityType, EntityDB, BaseEntity, SearchEntityBy, GroupEntity, CompanyEntitySection, BranchesFuzzyMatch, ClassificationCompanyEntity, NettingEngineEntity, FunctionMigration } from './types';
import { Agency, Eye, Group, Home, OfficeBuilding } from '../../../shared/icons';
import { getEntityRoot } from '../../store/selectors';

const getIsCompleted = (entity: ClassificationCompanyEntity) => {
    const { MiFIDClassification, counterpartyType, opinionCounterpartyType, emirClassification, entityServiceType } = entity;
    return [MiFIDClassification, counterpartyType, opinionCounterpartyType, emirClassification, entityServiceType].every(field => !isUndefined(field) && ((field.value && !field.aiPopulated) || field.aiConfirmed));
};

export const getModalOpen = (state: ApplicationState): EntityModalType | null => getEntityRoot(state).modalOpen;

export const getIsSaving = (state: ApplicationState): boolean => getEntityRoot(state).isSaving;

export const getCurrentEntity = (state: ApplicationState): Entity | null => getEntityRoot(state).currentEntity;

export const getSavedEntity = (state: ApplicationState): Entity | null => getEntityRoot(state).savedEntity;

export const getIsSearching = (state: ApplicationState): boolean => getEntityRoot(state).searchingEntities;

export const getSearchResults = (state: ApplicationState): Lei[] => getEntityRoot(state).searchResults;

export const getAllEntities = (state: ApplicationState): BaseEntity[] => getEntityRoot(state).allEntities;

export const getNettingEntities = (state: ApplicationState): NettingEngineEntity[] => getEntityRoot(state).nettingEntities;

export const getBaseEntityDropdownOptions = (state: ApplicationState): DropdownOption[] => {
    const getIcon = (myCompany: number, onWatchlist: number, canBeAgent: number) => {
        if (myCompany === 1) {
            return Home;
        }
        if (onWatchlist === 1) {
            return Eye;
        }
        if (canBeAgent === 1) {
            return Agency;
        }
        return OfficeBuilding;
    };
    const allEntities = getAllEntities(state);
    const groupEntities = allEntities.length > 0 && allEntities.filter(({ type }) => type === EntityType.GROUP) || [];
    const myGroupEntities = groupEntities.filter(({ myCompany }) => myCompany === 1);
    const counterpartyGroupEntities = groupEntities.filter(({ myCompany }) => myCompany === 0);
    const myGroupEntityOptions = myGroupEntities.length > 0 ? myGroupEntities.map(({ name, entityId }, index) => ({ value: entityId.toString(), label: name, showDivider: index === myGroupEntities.length - 1, icon: Home })) : [];
    const counterpartyGroupEntityOptions = counterpartyGroupEntities.length > 0 ? counterpartyGroupEntities.map(({ name, entityId }, index) => ({ value: entityId.toString(), label: name, showDivider: index === counterpartyGroupEntities.length - 1, icon: Group })) : [];
    const companyEntities = allEntities.length > 0 && allEntities.filter(({ type }) => type === EntityType.COMPANY) || [];
    const companyEntityOptions = companyEntities.length > 0 ? companyEntities.map(({ name, entityId, myCompany, onWatchlist, canBeAgent }) => ({ value: entityId.toString(), label: name, icon: getIcon(myCompany, onWatchlist, canBeAgent) })) : [];
    const entityOptions = [WATCHLIST_ENTITY, ...myGroupEntityOptions, ...counterpartyGroupEntityOptions, ...companyEntityOptions];
    return entityOptions;
};

export const getPaginatedEntities = (state: ApplicationState): EntityDB[] => getEntityRoot(state).paginatedEntities;

export const getAllCompanyEntityNames = (state: ApplicationState): string[] => getEntityRoot(state).allEntities.filter(({ type }) => type === EntityType.COMPANY).map(({ name }) => name);

export const getIsFetchingEntities = (state: ApplicationState): boolean => getEntityRoot(state).fetchingEntities;

export const getSearchEntityType = (state: ApplicationState): SearchEntityBy => getEntityRoot(state).searchBy;

export const getSearchEntityValue = (state: ApplicationState): string => getEntityRoot(state).searchInput;

export const getMyCompanyEntities = (state: ApplicationState): BaseEntity[] => getEntityRoot(state).myCompanyEntities;

export const getMyEntityGroups = (state: ApplicationState): GroupEntity[] => getEntityRoot(state).groupEntities;

export const getEntityIdsForGroup = (state: ApplicationState): string[] => getEntityRoot(state).entityIdsForGroup;

export const getCompanyEntityGroups = (state: ApplicationState): string[] => getEntityRoot(state).companyEntityGroupIds;

export const getFunctionMigration = (state: ApplicationState): FunctionMigration | null => getEntityRoot(state).doraFunctionMigration;

export const getMigrateFunctionConfirmationModalOpen = (state: ApplicationState): boolean => {
    const functionMigration = getFunctionMigration(state);
    return !isNull(functionMigration) && functionMigration.migrations.length > 0;
};

export const getSelectedCompanySection = (state: ApplicationState): CompanyEntitySection => getEntityRoot(state).selectedCompanySection;

export const getBranchesFuzzyMatchModalOpen = (state: ApplicationState): number | null => getEntityRoot(state).fuzzyMatchModalOpen;

export const getBranchesFuzzyMatchResults = (state: ApplicationState): BranchesFuzzyMatch[] => getEntityRoot(state).fuzzyMatchResults;

export const getClassificationFilter = (state: ApplicationState): string => getEntityRoot(state).classificationFilter;

export const getShowClassificationCompleted = (state: ApplicationState): boolean => getEntityRoot(state).showCompleted;

export const getSavedClassificationEntities = (state: ApplicationState): ClassificationCompanyEntity[] => getEntityRoot(state).savedClassificationEntities;

export const getCompletedClassificationEntityIds = (state: ApplicationState): number[] => {
    const savedClassificationEntities = getSavedClassificationEntities(state);
    return savedClassificationEntities.filter(entity => getIsCompleted(entity)).map(({ entityId }) => entityId);
};

export const getCurrentClassificationEntities = (state: ApplicationState): ClassificationCompanyEntity[] => {
    const allEntities = getEntityRoot(state).currentClassificationEntities;
    const nameFilter = getClassificationFilter(state);
    const showCompleted = getShowClassificationCompleted(state);
    const completedEntityIds = getCompletedClassificationEntityIds(state);
    return allEntities.filter(({ entityId, name }) => name.toLowerCase().includes(nameFilter.toLowerCase()) && (showCompleted || (!showCompleted && !completedEntityIds.includes(entityId))));
};

export const getCurrentClassifyingEntityIds = (state: ApplicationState): number[] => getEntityRoot(state).assertingEntityClassification;

export const getEntityClassificationHasUpdated = (state: ApplicationState): boolean => getEntityRoot(state).entityClassificationHasUpdated;

export const getEntityClassificationCloseModalOpen = (state: ApplicationState): boolean => getEntityRoot(state).closeClassificationConfirmationModalOpen;

export const getFuzzyCompletions = (state: ApplicationState): string[] => getEntityRoot(state).fuzzyCompletions;

export const getAutoCompletions = (state: ApplicationState): string[] => getEntityRoot(state).autoCompletions;

export const getShowCompletions = (state: ApplicationState): boolean => getEntityRoot(state).showCompletions;

export const getShowFuzzyAutoCompletions = (state: ApplicationState): boolean => (getFuzzyCompletions(state).length > 0 || getAutoCompletions(state).length > 0) && getShowCompletions(state);

export const getEntityToDelete = (state: ApplicationState): EntityDB | null => getEntityRoot(state).entityToDelete;

export const getIsDeleting = (state: ApplicationState): boolean => getEntityRoot(state).isDeleting;

// Table Filters and Pagination
export const getEntitiesPageNumber = (state: ApplicationState): number => getEntityRoot(state).entityPage;
export const getTotalEntities = (state: ApplicationState): number => getEntityRoot(state).totalEntities;
export const getEntityFilters = (state: ApplicationState): TableFilters => getEntityRoot(state).entityFilters;
export const getEntityColumnSort = (state: ApplicationState): ColumnSort | undefined => getEntityRoot(state).entitySort;
export const getEntitiesPageSize = (state: ApplicationState): number => getEntityRoot(state).entityPageSize;

export const getEntitiesSearchPageNumber = (state: ApplicationState): number => getEntityRoot(state).searchEntityPage;
export const getTotalSearchEntities = (state: ApplicationState): number => getEntityRoot(state).totalSearchEntities;
export const getEntitiesSearchPageSize = (state: ApplicationState): number => getEntityRoot(state).searchEntityPageSize;
